<template>
    <div class="games-filter games-filter--desktop" v-click-outside="handleClickOutside">
        <div class="games-filter__group">
            <div
                v-for="filter in filterCategories"
                :key="filter.category"
                class="games-filter__option"
                :class="{
                    'games-filter__option--open': openFilterCategory === filter.category,
                    'games-filter__option--small': filterOptions[filter.category].length < 10,
                }"
            >
                <DsButton
                    type="interaction"
                    size="sm"
                    theme="dark"
                    icon-right="chevron-down"
                    @click="toggleFilter(filter.category)"
                    >{{ filter.text }}
                    <div v-if="filterParameters[filter.category]?.length" class="games-filter__option-number">
                        {{ filterParameters[filter.category].length }}
                    </div></DsButton
                >

                <GamesFilterCategoryOptions
                    v-if="openFilterCategory === filter.category"
                    class="games-filter__option-dialog"
                    :options="filterOptions[filter.category]"
                    v-model="filterParameters[filter.category]"
                    :name="filter.category"
                    @filterChanged="(item: GamesFilterCategory) => applyFilter(filter.category, item)"
                />
            </div>
        </div>

        <div
            class="games-filter__option games-filter__option--sort"
            :class="{ 'games-filter__option--open': isSortOpen }"
        >
            <DsButton
                v-if="showSortLabel"
                type="interaction"
                size="sm"
                theme="dark"
                icon-left="sort-by"
                @click="toggleSort"
            >
                {{ sortLabel }}
            </DsButton>
            <DsButton v-else type="interaction" size="sm" theme="dark" icon-left="sort-by" @click="toggleSort">
            </DsButton>

            <GamesSortOptions
                v-if="isSortOpen"
                class="games-filter__option-dialog games-filter__option-dialog--sort"
                @sort-applied="closeSort"
            />
        </div>
    </div>
</template>

<script lang="ts" setup>
import { computed, ref } from "vue";
import { useGamesGrid } from "@/games/composables/use-games-grid";
import DsButton from "@sazka/design-system/src/button/ds-button.vue";
import GamesFilterCategoryOptions from "./games-filter-category-options.vue";
import GamesSortOptions from "./games-sort-options.vue";
import { useBreakpoints } from "@sazka/utils/composables/use-breakpoints";
import { vClickOutside } from "@sazka/utils/directives/v-click-outside";
import { GamesFilterType } from "@/games/models/games-filter-type";
import { GamesAnalytics } from "@/games/analytics/analytics-games";
import { GamesFilterCategory } from "@/games/models/games-filter-options";

const { reloadWithFilter, filterOptions, filterParameters, getFilterSortTypeLabel } = useGamesGrid();

const filterCategories = computed(() => [
    {
        category: GamesFilterType.Genres,
        text: "Žánr",
    },
    {
        category: GamesFilterType.Manufacturers,
        text: "Výrobce",
    },
    {
        category: GamesFilterType.Reels,
        text: "Počet válců",
    },
    {
        category: GamesFilterType.Lines,
        text: "Počet linií",
    },
]);

const openFilterCategory = ref<GamesFilterType>(null);
const isSortOpen = ref(false);
const sortLabel = ref<string>("Seřadit");
const showSortLabel = useBreakpoints("sm2");

const toggleFilter = (category: GamesFilterType) => {
    if (category === openFilterCategory.value) {
        openFilterCategory.value = null;
    } else {
        isSortOpen.value = false;
        openFilterCategory.value = category;
        GamesAnalytics.pushDesktopFilterOpen(category);
    }
};

const toggleSort = () => {
    if (!isSortOpen.value) {
        openFilterCategory.value = null;
    }

    isSortOpen.value = !isSortOpen.value;
};

const closeSort = () => {
    sortLabel.value = getFilterSortTypeLabel(filterParameters.value.sortType);
    isSortOpen.value = false;
};

const handleClickOutside = () => {
    openFilterCategory.value = null;
    isSortOpen.value = false;
};

const applyFilter = async (type: GamesFilterType, item: GamesFilterCategory) => {
    const allAppliedFilterNames = GamesAnalytics.getFilterOptionNames(
        filterParameters.value.genres,
        filterParameters.value.manufacturers,
        filterParameters.value.reels,
        filterParameters.value.lines,
        filterOptions.value
    );

    if (filterParameters.value[type].includes(item.guid)) {
        GamesAnalytics.pushFilterApplied(type, item.name, allAppliedFilterNames);
    } else {
        GamesAnalytics.pushFilterRemoved(type, [item.name], allAppliedFilterNames);
    }

    await reloadWithFilter();
};
</script>
